'use client';

import { useState, useEffect } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    EyeIcon,
    GlobeAltIcon,
    UserGroupIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { normalizeCountry, getCountryDisplay } from '@/lib/utils/countryNormalization';

interface AnalyticsData {
    totalVisits: number;
    onlineUsers: number;
    countriesData: Array<{ country: string; visitors: number }>;
    pageViewsData: Array<{ page: string; views: number }>;
    referrerStats: Array<{ referrer: string; count: number }>;
}

interface VisitorDetail {
    _id: string;
    sessionId: string;
    ipAddress: string;
    country?: string;
    city?: string;
    browser: string;
    os: string;
    device: string;
    referrer?: string;
    currentPage: string;
    startTime: string;
    lastActivity: string;
    duration?: number;
    pageViews: number;
    isActive: boolean;
}

interface RealTimeStats {
    onlineUsers: number;
    activeCountries: string[];
    recentVisitors: Array<{
        country: string;
        page: string;
        timestamp: string;
    }>;
    topActivePages: Array<{
        page: string;
        activeUsers: number;
    }>;
}

// Colorful chart colors for better differentiation
const COLORS = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#F97316', // Orange
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6366F1', // Indigo
    '#14B8A6', // Teal
    '#F43F5E', // Rose
];

export default function AnalyticsDashboard() {
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [visitorDetails, setVisitorDetails] = useState<VisitorDetail[]>([]);
    const [realtimeStats, setRealtimeStats] = useState<RealTimeStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Filter and sorting state
    const [filters, setFilters] = useState({
        status: 'all',
        country: 'all',
        city: 'all',
        referrer: 'all',
        os: 'all',
        currentPage: 'all'
    });
    const [sortBy, setSortBy] = useState('lastActivity');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        fetchAnalyticsData();
        fetchVisitorDetails();
        fetchRealtimeStats();

        // Set up real-time updates
        const interval = setInterval(() => {
            fetchRealtimeStats();
            if (activeTab === 'visitors') {
                fetchVisitorDetails();
            }
            if (activeTab === 'overview') {
                fetchAnalyticsData();
            }
        }, 15000); // Update every 15 seconds

        return () => clearInterval(interval);
    }, [activeTab]);

    const fetchAnalyticsData = async () => {
        try {
            const response = await fetch('/api/analytics/stats?type=overview');
            if (response.ok) {
                const data = await response.json();
                setAnalyticsData(data);
            }
        } catch (error) {
            console.error('Failed to fetch analytics data:', error);
        }
    };

    const fetchVisitorDetails = async () => {
        try {
            const response = await fetch('/api/analytics/stats?type=visitors&limit=100');
            if (response.ok) {
                const data = await response.json();
                setVisitorDetails(data);
            }
        } catch (error) {
            console.error('Failed to fetch visitor details:', error);
        }
    };

    const fetchRealtimeStats = async () => {
        try {
            const response = await fetch('/api/analytics/stats?type=realtime');
            if (response.ok) {
                const data = await response.json();
                setRealtimeStats(data);
            }
        } catch (error) {
            console.error('Failed to fetch realtime stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (seconds?: number): string => {
        if (!seconds) return '0s';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const formatTimeAgo = (timestamp: string): string => {
        const now = new Date();
        const time = new Date(timestamp);
        const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return `${diffInSeconds}s ago`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)}m ago`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)}h ago`;
        } else {
            return `${Math.floor(diffInSeconds / 86400)}d ago`;
        }
    };

    // Get unique values for filter options
    const getUniqueValues = (field: keyof VisitorDetail) => {
        const values = new Set(visitorDetails.map(visitor => visitor[field] as string).filter(Boolean));
        return Array.from(values).sort();
    };

    // Apply filters and sorting
    const filteredAndSortedVisitors = visitorDetails
        .filter(visitor => {
            if (filters.status !== 'all' && 
                ((filters.status === 'online' && !visitor.isActive) || 
                 (filters.status === 'offline' && visitor.isActive))) {
                return false;
            }
            if (filters.country !== 'all' && visitor.country !== filters.country) return false;
            if (filters.city !== 'all' && visitor.city !== filters.city) return false;
            if (filters.referrer !== 'all' && visitor.referrer !== filters.referrer) return false;
            if (filters.os !== 'all' && visitor.os !== filters.os) return false;
            if (filters.currentPage !== 'all' && visitor.currentPage !== filters.currentPage) return false;
            return true;
        })
        .sort((a, b) => {
            let aValue: string | number | boolean | undefined = a[sortBy as keyof VisitorDetail] as string | number | boolean | undefined;
            let bValue: string | number | boolean | undefined = b[sortBy as keyof VisitorDetail] as string | number | boolean | undefined;
            
            // Handle special cases
            if (sortBy === 'lastActivity' || sortBy === 'startTime') {
                aValue = new Date(aValue as string).getTime();
                bValue = new Date(bValue as string).getTime();
            } else if (sortBy === 'duration') {
                aValue = (aValue as number) || 0;
                bValue = (bValue as number) || 0;
            } else if (sortBy === 'pageViews') {
                aValue = (aValue as number) || 0;
                bValue = (bValue as number) || 0;
            }
            
            if (sortOrder === 'asc') {
                return (aValue as number) > (bValue as number) ? 1 : -1;
            } else {
                return (aValue as number) < (bValue as number) ? 1 : -1;
            }
        });

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-black">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white p-6">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl font-bold mb-8 text-center text-white">
                    Analytics Dashboard
                </h1>

                {/* Real-time Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm">Online Users</p>
                                <p className="text-2xl font-bold text-white">
                                    {realtimeStats?.onlineUsers || 0}
                                </p>
                            </div>
                            <UserGroupIcon className="h-8 w-8 text-white" />
                        </div>
                    </div>

                    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm">Total Visits</p>
                                <p className="text-2xl font-bold text-white">
                                    {analyticsData?.totalVisits || 0}
                                </p>
                            </div>
                            <EyeIcon className="h-8 w-8 text-white" />
                        </div>
                    </div>

                    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm">Active Countries</p>
                                <p className="text-2xl font-bold text-white">
                                    {realtimeStats?.activeCountries?.length || 0}
                                </p>
                            </div>
                            <GlobeAltIcon className="h-8 w-8 text-white" />
                        </div>
                    </div>

                    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm">Page Views</p>
                                <p className="text-2xl font-bold text-white">
                                    {analyticsData?.pageViewsData?.reduce((sum, page) => sum + page.views, 0) || 0}
                                </p>
                            </div>
                            <ClockIcon className="h-8 w-8 text-white" />
                        </div>
                    </div>
                </div>

                {/* Local IP Notice - Only show in development */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6">
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                            <p className="text-gray-300 text-sm">
                                <strong>Local Development Mode:</strong> Analytics tracking is disabled for local/private IP addresses.
                                Deploy to production to see real visitor data.
                            </p>
                        </div>
                    </div>
                )}

                {/* Referrer Stats */}
                {analyticsData?.referrerStats && analyticsData.referrerStats.length > 0 && (
                    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 mb-8">
                        <h3 className="text-xl font-semibold mb-4 text-white">Referrer Statistics</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {['R4b', 'Ash', 'Misa'].map((referrer) => {
                                const stat = analyticsData.referrerStats.find(r => r.referrer === referrer);
                                const count = stat?.count || 0;
                                return (
                                    <div key={referrer} className="bg-gray-800 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-white">{count}</div>
                                        <div className="text-gray-300 text-sm">{referrer}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 text-sm text-gray-400">
                            Total referral visits: {analyticsData.referrerStats.reduce((sum, r) => sum + r.count, 0)} (Production only)
                        </div>
                    </div>
                )}

                {/* Navigation Tabs */}
                <div className="flex space-x-1 mb-8 bg-gray-900 p-1 rounded-lg border border-gray-800">
                    {['overview', 'visitors', 'realtime'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab
                                ? 'bg-white text-black'
                                : 'text-gray-300 hover:text-white hover:bg-gray-800'
                                }`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-8">
                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Visitors by Country */}
                            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                                <h3 className="text-xl font-semibold mb-4 text-white">Visitors by Country</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={analyticsData?.countriesData || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis 
                                            dataKey="country" 
                                            stroke="#9CA3AF"
                                            tickFormatter={(value) => {
                                                const normalized = normalizeCountry(value);
                                                return normalized.flag;
                                            }}
                                        />
                                        <YAxis stroke="#9CA3AF" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#111827',
                                                border: '1px solid #374151',
                                                borderRadius: '6px',
                                                color: '#FFFFFF'
                                            }}
                                            labelFormatter={(value) => {
                                                const normalized = normalizeCountry(value);
                                                return `${normalized.flag} ${normalized.name}`;
                                            }}
                                        />
                                        <Bar dataKey="visitors" fill="#3B82F6" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Page Views */}
                            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                                <h3 className="text-xl font-semibold mb-4 text-white">Page Views</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={analyticsData?.pageViewsData || []}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={({ name, percent }) => `${name ?? 'Unknown'} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="views"
                                        >
                                            {(analyticsData?.pageViewsData || []).map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#111827',
                                                border: '1px solid #374151',
                                                borderRadius: '6px',
                                                color: '#FFFFFF'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                
                                {/* Legend */}
                                <div className="mt-4 flex flex-wrap gap-4 justify-center">
                                    {(analyticsData?.pageViewsData || []).map((entry, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <div 
                                                className="w-4 h-4 rounded"
                                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                            ></div>
                                            <span className="text-sm text-gray-300">{entry.page}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Referrer Chart */}
                        {analyticsData?.referrerStats && analyticsData.referrerStats.length > 0 && (
                            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                                <h3 className="text-xl font-semibold mb-4 text-white">Referrer Distribution</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={analyticsData.referrerStats}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="referrer" stroke="#9CA3AF" />
                                        <YAxis stroke="#9CA3AF" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#111827',
                                                border: '1px solid #374151',
                                                borderRadius: '6px',
                                                color: '#FFFFFF'
                                            }}
                                        />
                                        <Bar dataKey="count">
                                            {analyticsData.referrerStats.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={
                                                        entry.referrer === 'Ash' ? '#10B981' : // Green
                                                        entry.referrer === 'R4b' ? '#3B82F6' : // Blue
                                                        entry.referrer === 'Misa' ? '#EF4444' : // Red
                                                        COLORS[index % COLORS.length] // Fallback
                                                    } 
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                                
                                {/* Referrer Legend */}
                                <div className="mt-4 flex flex-wrap gap-4 justify-center">
                                    {analyticsData.referrerStats.map((entry, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <div 
                                                className="w-4 h-4 rounded"
                                                style={{ 
                                                    backgroundColor: 
                                                        entry.referrer === 'Ash' ? '#10B981' : // Green
                                                        entry.referrer === 'R4b' ? '#3B82F6' : // Blue
                                                        entry.referrer === 'Misa' ? '#EF4444' : // Red
                                                        COLORS[index % COLORS.length] // Fallback
                                                }}
                                            ></div>
                                            <span className="text-sm text-gray-300">{entry.referrer}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Visitors Tab */}
                {activeTab === 'visitors' && (
                    <div className="bg-gray-900 rounded-lg border border-gray-800">
                        <div className="p-6">
                            <h3 className="text-xl font-semibold mb-4 text-white">Visitor Details</h3>
                            
                            {/* Filters */}
                            <div className="mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {/* Status Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All</option>
                                        <option value="online">Online</option>
                                        <option value="offline">Offline</option>
                                    </select>
                                </div>

                                {/* Country Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                                    <select
                                        value={filters.country}
                                        onChange={(e) => setFilters({...filters, country: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All Countries</option>
                                        {getUniqueValues('country').map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* City Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                                    <select
                                        value={filters.city}
                                        onChange={(e) => setFilters({...filters, city: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All Cities</option>
                                        {getUniqueValues('city').map(city => (
                                            <option key={city} value={city}>{city}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Referrer Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Referrer</label>
                                    <select
                                        value={filters.referrer}
                                        onChange={(e) => setFilters({...filters, referrer: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All Referrers</option>
                                        {getUniqueValues('referrer').map(referrer => (
                                            <option key={referrer} value={referrer}>{referrer}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* OS Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">OS</label>
                                    <select
                                        value={filters.os}
                                        onChange={(e) => setFilters({...filters, os: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All OS</option>
                                        {getUniqueValues('os').map(os => (
                                            <option key={os} value={os}>{os}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Current Page Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Page</label>
                                    <select
                                        value={filters.currentPage}
                                        onChange={(e) => setFilters({...filters, currentPage: e.target.value})}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="all">All Pages</option>
                                        {getUniqueValues('currentPage').map(page => (
                                            <option key={page} value={page}>{page}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Results Count */}
                            <div className="mb-4 text-sm text-gray-400">
                                Showing {filteredAndSortedVisitors.length} of {visitorDetails.length} visitors
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-700">
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('isActive')}
                                            >
                                                Status {sortBy === 'isActive' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('country')}
                                            >
                                                Country {sortBy === 'country' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('city')}
                                            >
                                                City {sortBy === 'city' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('referrer')}
                                            >
                                                Referrer {sortBy === 'referrer' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th className="text-left py-3 px-4 text-gray-300">Browser</th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('os')}
                                            >
                                                OS {sortBy === 'os' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th className="text-left py-3 px-4 text-gray-300">Device</th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('currentPage')}
                                            >
                                                Current Page {sortBy === 'currentPage' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('duration')}
                                            >
                                                Duration {sortBy === 'duration' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('pageViews')}
                                            >
                                                Page Views {sortBy === 'pageViews' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                className="text-left py-3 px-4 text-gray-300 cursor-pointer hover:text-white"
                                                onClick={() => handleSort('lastActivity')}
                                            >
                                                Last Activity {sortBy === 'lastActivity' && (sortOrder === 'asc' ? '↑' : '↓')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAndSortedVisitors.map((visitor) => (
                                            <tr key={visitor._id} className="border-b border-gray-700 hover:bg-gray-800">
                                                <td className="py-3 px-4">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${visitor.isActive
                                                        ? 'bg-green-900 text-green-200'
                                                        : 'bg-gray-700 text-gray-300'
                                                        }`}>
                                                        {visitor.isActive ? 'Online' : 'Offline'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-white">
                                                    {getCountryDisplay(visitor.country || 'Unknown')}
                                                </td>
                                                <td className="py-3 px-4 text-gray-300">{visitor.city || 'Unknown'}</td>
                                                <td className="py-3 px-4">
                                                    {visitor.referrer ? (
                                                        <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs">
                                                            {visitor.referrer}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-gray-300">{visitor.browser}</td>
                                                <td className="py-3 px-4 text-gray-300">{visitor.os}</td>
                                                <td className="py-3 px-4 text-gray-300">{visitor.device}</td>
                                                <td className="py-3 px-4 text-blue-400">{visitor.currentPage}</td>
                                                <td className="py-3 px-4 text-gray-300">{formatDuration(visitor.duration)}</td>
                                                <td className="py-3 px-4 text-gray-300">{visitor.pageViews}</td>
                                                <td className="py-3 px-4 text-gray-300">{formatTimeAgo(visitor.lastActivity)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Real-time Tab */}
                {activeTab === 'realtime' && (
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Recent Visitors */}
                            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                                <h3 className="text-xl font-semibold mb-4 text-white">Recent Visitors</h3>
                                <div className="space-y-3">
                                    {realtimeStats?.recentVisitors?.map((visitor, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                                                <span className="font-medium text-white">
                                                    {getCountryDisplay(visitor.country)}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-blue-400 text-sm">{visitor.page}</div>
                                                <div className="text-gray-400 text-xs">{formatTimeAgo(visitor.timestamp)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Top Active Pages */}
                            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                                <h3 className="text-xl font-semibold mb-4 text-white">Top Active Pages</h3>
                                <div className="space-y-3">
                                    {realtimeStats?.topActivePages?.map((page, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                                            <span className="font-medium text-blue-400">{page.page}</span>
                                            <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-sm">
                                                {page.activeUsers} users
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
